import React from 'react';

import { useNavigate } from 'react-router-dom';

import {
	ActionIcon,
	Box,
	Button,
	Center,
	createModal,
	createStyles,
	EntityBadge,
	Flex,
	Icon,
	Loader,
	ScrollArea,
	Stack,
	TextInput,
	TFCard,
	TFDataTable,
	TFEmptyStateMessage,
	TFListCard,
	TFText,
	Title,
	UnstyledButton,
	useDebounce,
	useDisclosure,
	useModalControl,
} from '@tf/ui';
import { fmt } from '@tf/utils';

import { getNodeIdByObjectUri } from '@/core/api/legacy';
import {
	useCreateConnectionMutation,
	useProfileConnections,
	useProfilesLookupQuery,
	useRemoveConnectionMutation,
} from '@/core/api/profiles';
import { useGetEntityLabel, useSelectedAccount } from '@/core/hooks';
import { useProfileKindPossibleConnections } from '@/components/profiles/useProfileKindPossibleConnections';
import { ModalFooter } from '@/components/shared';

const useStyles = createStyles(({ colors }) => ({
	title: {
		color: colors.gray[9],
		fontWeight: 500,
		fontSize: 14,
		lineHeight: 'normal',
	},
	subtitle: {
		marginTop: '4px',
		color: colors.gray[4],
		fontWeight: 500,
		fontSize: 13,
		lineHeight: 'normal',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	accountName: {
		fontSize: 13,
		height: 32,
		display: 'flex',
		alignItems: 'center',
		color: colors.brand[6],
		'&:hover': {
			color: colors.brand[6],
			textDecoration: 'underline',
		},
	},
}));

export const ConnectionsTable = () => {
	const { classes } = useStyles();
	const navigate = useNavigate();
	const account = useSelectedAccount();
	const { data, isPending, isError } = useProfileConnections(account.profileId);

	const possibleConnections = useProfileKindPossibleConnections(account.meta.entityKind);

	const createConnectionModal = useDisclosure();
	const removeConnectionMutation = useRemoveConnectionMutation();

	const connectionLabel = possibleConnections.label.toLowerCase().slice(0, -1);

	if (isError) {
		return <ConnectionsTableError />;
	}

	return (
		<Box mt="1.5rem">
			<Flex justify="space-between" align="center" mb="0.75rem">
				<Title order={3}>{possibleConnections.label}</Title>
				<Button onClick={createConnectionModal.open}>Link {connectionLabel}</Button>
			</Flex>
			<TFDataTable<{ id: string; connectionId: string; name: string; createdAt: string }>
				withClientPagination
				name="profile-connections-table"
				noDataMessage={{
					icon: <Icon.IconLinkPlus />,
					title: possibleConnections.label,
					description: `You haven't link any ${possibleConnections.label.toLowerCase()} yet`,
					primaryAction: (
						<Button onClick={createConnectionModal.open} leftSection={<Icon.IconPlus size={16} />}>
							Link {connectionLabel}
						</Button>
					),
				}}
				data={data ?? []}
				isLoading={isPending}
				defs={[
					{
						header: 'Name',
						accessorKey: 'name',
						size: 200,
						Cell: ({ cell }) => {
							const { name, id } = cell.row.original;
							return (
								<UnstyledButton
									onClick={async () => {
										const nodeId = await getNodeIdByObjectUri(id);
										navigate(`/accounts/${nodeId}`);
									}}
									className={classes.accountName}
								>
									<TFText inherit>{name}</TFText>
								</UnstyledButton>
							);
						},
					},
					{
						header: 'Linked on',
						accessorKey: 'createdAt',
						size: 200,
						accessorFn: (item) => fmt.toDate(item.createdAt, { preset: 'full_date_with_time' }),
					},
					{
						header: `Unlink ${connectionLabel}`,
						accessorKey: 'unlink',
						size: 75,
						enableSorting: false,
						enableResizing: false,
						Cell: ({ row }) => {
							const { connectionId } = row.original;
							const isRemoving =
								removeConnectionMutation.isPending &&
								removeConnectionMutation.variables?.connectionId === connectionId;

							return (
								<ActionIcon
									variant="subtle"
									color="gray"
									onClick={async () => {
										await removeConnectionMutation.mutateAsync({
											connectionId,
											profileId: account.profileId,
										});
									}}
									disabled={isRemoving}
									loading={isRemoving}
								>
									<Icon.IconUnlink size={16} />
								</ActionIcon>
							);
						},
					},
				]}
			/>
			<CreateConnectionModal
				size="500px"
				title={`Link ${possibleConnections.label.toLowerCase().slice(0, -1)}`}
				opened={createConnectionModal.isOpen}
				onClose={createConnectionModal.close}
			/>
		</Box>
	);
};

const CreateConnectionModal = createModal(() => {
	const account = useSelectedAccount();

	const { onClose } = useModalControl();

	const createConnectionMutation = useCreateConnectionMutation();
	const possibleConnections = useProfileKindPossibleConnections(account.meta.entityKind);

	const { classes } = useStyles();
	const getEntityLabel = useGetEntityLabel();

	const [searchValue, setSearchValue] = React.useState('');
	const [selectedProfileId, setSelectedProfileId] = React.useState<string | null>(null);

	const query = useDebounce(searchValue, 250);
	const { data: suggestedProfiles, isFetching } = useProfilesLookupQuery(
		possibleConnections.connections,
		query
	);

	const { data: connectedProfiles } = useProfileConnections(account.profileId);
	const filteredSuggestedProfiles = suggestedProfiles?.filter(
		(profile) => !connectedProfiles?.some((p) => p.id === profile.id)
	);

	const connectProfile = async () => {
		if (!selectedProfileId) return;

		await createConnectionMutation.mutateAsync(
			{ profileAId: account.profileId, profileBId: selectedProfileId },
			{ onSuccess: onClose }
		);
	};

	return (
		<>
			<Stack gap="0" m=".5rem .75rem .75rem">
				<TextInput
					label="Name"
					placeholder="Search"
					value={searchValue}
					onChange={(e) => {
						setSearchValue(e.target.value);
						setSelectedProfileId(null);
					}}
					rightSection={isFetching && <Loader variant="oval" size="xs" />}
				/>
				{!filteredSuggestedProfiles?.length && query.length > 1 && (
					<TFText pb="4px" className={classes.subtitle}>
						No results found. Please check the spelling
					</TFText>
				)}

				{filteredSuggestedProfiles && filteredSuggestedProfiles?.length > 0 && (
					<ScrollArea mt="12px" h={filteredSuggestedProfiles.length > 10 ? '500px' : 'auto'}>
						<Stack gap="8px">
							{filteredSuggestedProfiles.map(({ id, labels, kind }) => {
								const isSelected = id === selectedProfileId;
								return (
									<TFListCard
										onClick={() => {
											if (isSelected) setSelectedProfileId(null);
											else setSelectedProfileId(id);
										}}
										isActive={selectedProfileId === id}
										key={id}
										type="radio"
									>
										<Flex align="center" ml="12px" w="100%" justify="space-between">
											<Box mr="12px" maw="280px">
												<TFText className={classes.title}>{labels.name}</TFText>
											</Box>
											<EntityBadge kind={getEntityLabel(kind)} />
										</Flex>
									</TFListCard>
								);
							})}
						</Stack>
					</ScrollArea>
				)}
			</Stack>
			{Boolean(filteredSuggestedProfiles?.length) && (
				<ModalFooter submitText="Add" onSubmit={connectProfile} isSticky />
			)}
		</>
	);
});

const ConnectionsTableError = () => {
	return (
		<TFCard mt="12px">
			<Center h={300}>
				<TFEmptyStateMessage
					icon={<Icon.IconUnlink />}
					title="Something went wrong"
					description="Try to refresh the page or come back later"
					primaryAction={
						<Button
							variant="light"
							size="md"
							leftSection={<Icon.IconRefresh size={18} />}
							onClick={() => window.location.reload()}
						>
							Refresh the page
						</Button>
					}
				/>
			</Center>
		</TFCard>
	);
};
